import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  UserCircle, KeyRound, Shield, ShieldCheck, ShieldOff, CreditCard,
  Smartphone, Monitor, Tablet, Trash2, Sparkles, Loader2, Clock,
  Scale, ChevronDown, Bug, Lightbulb, ShieldAlert, RefreshCw,
  Download, Share, Plus, CheckCircle2, LogOut, Menu as MenuIcon, BookOpen, Pencil, Check, Bell, LifeBuoy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL as API, BACKEND_URL } from '@/utils/apiConfig';
import PasswordInput from '@/components/PasswordInput';
import SharePlanManager from '@/components/SharePlanManager';
import BiometricDevicesPanel from '@/components/BiometricDevicesPanel';
import CollapsibleSettingsCard from '@/components/CollapsibleSettingsCard';
import { isDespiaRuntime, restoreApplePurchases } from '@/services/appleStoreKit';
import { isTWA } from '@/services/playBilling';
import { SectionHeader } from './SettingsHelpers';
import { mainTranslations, translationCredits, glassCard, glassOption } from './settingsConstants';
import { useSubscription } from '@/contexts/SubscriptionContext';

function PushReregisterButton() {
  const {
    t
  } = useTranslation();

  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const handleReregister = async () => {
    setLoading(true);
    setResult(null);
    try {
      // Step 1: Try to read the OneSignal subscription ID from Despia
      let subId = null;
      try {
        const { default: despia } = await import('despia-native');
        subId = despia.onesignalplayerid || window.onesignalplayerid;

        // Also fire the bridge command
        const userId = (await axios.get(`${API}/auth/me`, { withCredentials: true }))?.data?.user_id;
        if (userId) {
          despia(`setonesignalplayerid://?user_id=${encodeURIComponent(userId)}`);
        }
      } catch (err) {
        console.warn('[Push] Despia bridge unavailable:', err);
      }

      // Step 2: If we have the subscription ID, link it server-side
      if (subId && subId !== 'n/a') {
        const linkRes = await axios.post(`${API}/notifications/link-onesignal`, {
          subscription_id: subId
        }, { withCredentials: true });

        if (linkRes.data?.status === 'linked') {
          setResult({ success: true, msg: 'Device linked successfully! Push notifications should work now.' });
          toast.success(t('generated.accounttab_push_notifications_linked_45eb'));
        } else {
          setResult({ success: false, msg: `Link status: ${linkRes.data?.status}. ${linkRes.data?.detail || ''}` });
          toast.info(t('generated.accounttab_registration_sent_try_dd22'));
        }
      } else {
        // No subscription ID available — tell the user.
        setResult({
          success: false,
          msg: 'Could not read device ID. Make sure notifications are enabled in your device Settings, then restart the app and try again.'
        });
        toast.info(t('generated.accounttab_please_enable_notifications_3e5b'));
      }
    } catch (err) {
      setResult({ success: false, msg: 'Registration failed. Check your connection.' });
      toast.error(t('generated.accounttab_push_re_registration_e2ac'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <Button
        onClick={handleReregister}
        disabled={loading}
        className="w-full rounded-xl py-3 text-sm font-medium gap-2"
        style={{ background: 'var(--theme-accent)', color: '#fff' }}
        data-testid="reregister-push-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
        {loading ? 'Linking...' : 'Re-register Push Notifications'}
      </Button>
      {result && (
        <div className="p-3 rounded-xl text-xs" style={{
          background: result.success ? 'rgba(34,197,94,0.1)' : 'rgba(251,191,36,0.1)',
          border: `1px solid ${result.success ? 'rgba(34,197,94,0.3)' : 'rgba(251,191,36,0.3)'}`,
          color: result.success ? '#22c55e' : '#fbbf24',
        }} data-testid="push-reregister-result">
          {result.msg}
        </div>
      )}
    </div>
  );
}

export function AccountTab({
  user, accountInfo, username, setUsername,
  handleSaveUsername, handleProfilePicture, uploadingPicture, profilePicRef,
  currentPassword, setCurrentPassword, newPassword, setNewPassword,
  confirmPassword, setConfirmPassword, handleChangePassword, handleSetPassword, passwordLoading,
  twoFAEnabled, twoFASetup, setTwoFASetup, twoFACode, setTwoFACode, twoFALoading,
  handleSetup2FA, handleVerify2FA, handleDisable2FA,
  isPaid, tier, isShareOwner, isShareMember, shareOwnerName, subscription,
  subscriptionInfo, cancelLoading, handleCancelSubscription,
  devices, devicesLoading, maxDevices, currentDeviceId, removingDevice, removeDevice, renameDevice,
  showLicenses, setShowLicenses, showDeleteModal, setShowDeleteModal,
  hasAdminAccess,
  installPrompt, isAppInstalled, isIOS, showIOSInstructions, handleInstallClick,
  onClose,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [editingDeviceId, setEditingDeviceId] = React.useState(null);
  const [editingName, setEditingName] = React.useState('');
  const [restoringPurchases, setRestoringPurchases] = React.useState(false);
  const { refreshStatus } = useSubscription();

  // Apple App Store guideline 3.1.1 requires a distinct "Restore Purchases"
  // button that initiates the restore flow when tapped. This duplicates the
  // button already present on /plans so reviewers (and users) find it where
  // they expect — inside Settings → Account → Subscription.
  const handleRestorePurchases = async () => {
    setRestoringPurchases(true);
    try {
      const result = await restoreApplePurchases();
      if (result.restored) {
        toast.success(t('plans.purchases_restored', 'Purchases restored successfully!'));
        // Refresh subscription state so the UI reflects the restored entitlement
        if (refreshStatus) await refreshStatus();
      } else {
        toast.info(result.message || t('plans.no_purchases_found', 'No active purchases found.'));
      }
    } catch (err) {
      toast.error(err?.message || t('plans.restore_failed', 'Failed to restore purchases.'));
    } finally {
      setRestoringPurchases(false);
    }
  };

  const startRename = (device) => {
    setEditingDeviceId(device.device_id);
    setEditingName(device.device_name || '');
  };

  const saveRename = async () => {
    if (editingName.trim() && editingDeviceId) {
      await renameDevice(editingDeviceId, editingName.trim());
    }
    setEditingDeviceId(null);
    setEditingName('');
  };

  const isNativePlatform = isDespiaRuntime() || isTWA();
  const subSource = subscriptionInfo?.source;
  const isStripeSub = !!subscriptionInfo?.stripe_subscription_id;
  const isRevenueCatSub = subSource === 'revenuecat';
  const hasActiveSub = isPaid && (subscriptionInfo?.status === 'active' || subscriptionInfo?.status === 'canceling' || tier === 'paid' || tier === 'premium' || tier === 'share');

  return (
    <>
      {/* Username & Password Management */}
      {!user?.is_guest && accountInfo && (
        <div className="rounded-3xl p-6" style={glassCard} data-testid="settings-account-management">
          <SectionHeader icon={UserCircle} title={t('account.title')} />

          {/* Profile Picture */}
          <div className="flex items-center gap-4 mb-6 pb-6" style={{ borderBottom: '1px solid var(--glass-border)' }}>
            <button
              onClick={() => profilePicRef.current?.click()}
              className="relative group shrink-0"
              disabled={uploadingPicture}
              data-testid="profile-picture-btn"
            >
              {accountInfo.picture ? (
                <img src={accountInfo.picture.startsWith('http') ? accountInfo.picture : `${BACKEND_URL}${accountInfo.picture}`} alt="" className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <div className="h-16 w-16 rounded-full flex items-center justify-center text-xl font-bold" style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}>
                  {(accountInfo.name || accountInfo.username || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingPicture ? <Loader2 className="h-5 w-5 animate-spin text-white" /> : <UserCircle className="h-5 w-5 text-white" />}
              </div>
            </button>
            <input ref={profilePicRef} type="file" accept="image/*" className="hidden" onChange={handleProfilePicture} data-testid="profile-picture-input" />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{accountInfo.name || 'Your Name'}</p>
              <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.accounttab_tap_to_change_e8b6')}</p>
            </div>
          </div>

          {/* Username */}
          <div className="space-y-3 mb-6">
            <label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.username_label')}</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder={t('account.username_placeholder')}
                maxLength={30}
                className="flex-1 rounded-xl px-4 py-2.5 text-sm focus:outline-none"
                style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                data-testid="settings-username-input"
              />
              <Button
                onClick={handleSaveUsername}
                disabled={!username.trim() || username === accountInfo.username}
                size="sm" className="rounded-xl px-4"
                style={{ background: 'var(--theme-accent)', color: '#fff' }}
                data-testid="settings-save-username-btn"
              >
                {t('account.save')}
              </Button>
            </div>
            <p className="text-[11px]" style={{ color: 'var(--theme-textSecondary)', opacity: 0.7 }}>{t('account.username_hint')}</p>
          </div>

          {/* Change Password */}
          {accountInfo.has_password && (
            <div className="space-y-3 pt-4" style={{ borderTop: '1px solid var(--glass-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                <label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.change_password')}</label>
              </div>
              <PasswordInput value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder={t('account.current_password')} className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }} data-testid="settings-current-password" />
              <PasswordInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('account.new_password')} className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }} data-testid="settings-new-password" />
              <PasswordInput value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('account.confirm_password')} className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }} data-testid="settings-confirm-password" />
              <Button onClick={handleChangePassword} disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword} size="sm" className="rounded-xl" style={{ background: 'var(--theme-accent)', color: '#fff' }} data-testid="settings-change-password-btn">
                {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('account.update_password')}
              </Button>
            </div>
          )}

          {/* Set Password (Google-only) */}
          {!accountInfo.has_password && (
            <div className="space-y-3 pt-4" style={{ borderTop: '1px solid var(--glass-border)' }}>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                <label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.set_email_login')}</label>
              </div>
              <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.set_email_desc')}</p>
              <PasswordInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('account.new_password')} className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }} data-testid="settings-set-new-password" />
              <PasswordInput value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('account.confirm_pw_placeholder')} className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }} data-testid="settings-set-confirm-password" />
              <Button onClick={handleSetPassword} disabled={passwordLoading || !newPassword || !confirmPassword} size="sm" className="rounded-xl" style={{ background: 'var(--theme-accent)', color: '#fff' }} data-testid="settings-set-password-btn">
                {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('account.set_password')}
              </Button>
            </div>
          )}
        </div>
      )}
      {/* Two-Factor Authentication */}
      {!user?.is_guest && (
        <CollapsibleSettingsCard
          storageKey="2fa"
          tabId="account"
          searchTerms="security two factor authenticator authy google authenticator authy 2fa otp"
          glassCard={glassCard}
          testId="settings-2fa-section"
          icon={<Shield className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
          title={t('settings.security', 'Security')}
          subtitle={twoFAEnabled ? t('account.two_fa_enabled') : t('account.two_fa_disabled')}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {twoFAEnabled ? <ShieldCheck className="h-5 w-5 text-green-500" /> : <ShieldOff className="h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />}
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{t('account.two_fa')}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{twoFAEnabled ? t('account.two_fa_enabled') : t('account.two_fa_disabled')}</p>
                </div>
              </div>
              {!twoFASetup && (
                <Button
                  variant={twoFAEnabled ? 'ghost' : 'default'} size="sm"
                  onClick={twoFAEnabled ? () => setTwoFASetup({ disabling: true }) : handleSetup2FA}
                  disabled={twoFALoading} className="rounded-xl text-xs"
                  style={twoFAEnabled ? { color: 'var(--theme-textSecondary)' } : { background: 'var(--theme-accent)', color: '#fff' }}
                  data-testid="2fa-toggle-btn"
                >
                  {twoFALoading ? <Loader2 className="h-3 w-3 animate-spin" /> : twoFAEnabled ? t('account.disable') : t('account.enable')}
                </Button>
              )}
            </div>

            {/* Setup flow - QR */}
            {twoFASetup && !twoFASetup.disabling && (
              <div className="rounded-2xl p-4 space-y-4" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}>
                <p className="text-sm text-center" style={{ color: 'var(--theme-text)' }}>{t('account.scan_qr')}</p>
                <div className="flex justify-center">
                  <div className="bg-white p-2 rounded-xl" data-testid="user-2fa-qr">
                    <img src={`data:image/png;base64,${twoFASetup.qr_code}`} alt={t('generated.accounttab_2fa_qr_7ce6')} className="w-40 h-40" />
                  </div>
                </div>
                <p className="text-[11px] text-center font-mono tracking-wider" style={{ color: 'var(--theme-textSecondary)' }}>{twoFASetup.secret}</p>
                <div className="flex gap-2">
                  <input type="text" inputMode="numeric" maxLength={6} value={twoFACode} onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))} placeholder={t('account.enter_code')} className="flex-1 rounded-xl px-3 py-2.5 text-sm text-center font-mono tracking-widest focus:outline-none" style={{ background: 'var(--theme-background)', color: 'var(--theme-text)', border: '1px solid var(--glass-border)' }} data-testid="2fa-verify-input" />
                  <Button onClick={handleVerify2FA} disabled={twoFALoading || twoFACode.length !== 6} className="rounded-xl px-4" style={{ background: 'var(--theme-accent)', color: '#fff' }} data-testid="2fa-verify-btn">
                    {twoFALoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('account.verify')}
                  </Button>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setTwoFASetup(null); setTwoFACode(''); }} className="w-full rounded-xl text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.cancel')}</Button>
              </div>
            )}

            {/* Disable flow */}
            {twoFASetup?.disabling && (
              <div className="rounded-2xl p-4 space-y-3" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}>
                <p className="text-sm" style={{ color: 'var(--theme-text)' }}>{t('account.disable_prompt')}</p>
                <div className="flex gap-2">
                  <input type="text" inputMode="numeric" maxLength={6} value={twoFACode} onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))} placeholder="000000" className="flex-1 rounded-xl px-3 py-2.5 text-sm text-center font-mono tracking-widest focus:outline-none" style={{ background: 'var(--theme-background)', color: 'var(--theme-text)', border: '1px solid var(--glass-border)' }} data-testid="2fa-disable-input" />
                  <Button onClick={handleDisable2FA} disabled={twoFALoading || twoFACode.length !== 6} className="rounded-xl px-4" style={{ background: '#ef4444', color: '#fff' }} data-testid="2fa-disable-btn">
                    {twoFALoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('account.disable')}
                  </Button>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setTwoFASetup(null); setTwoFACode(''); }} className="w-full rounded-xl text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.cancel')}</Button>
              </div>
            )}
          </div>
        </CollapsibleSettingsCard>
      )}
      {/* Biometric Devices (Vault) — only renders when the user has a vault PIN */}
      {!user?.is_guest && <BiometricDevicesPanel glassCard={glassCard} />}
      {/* Subscription Management */}
      {!user?.is_guest && (
        <CollapsibleSettingsCard
          storageKey="subscription"
          tabId="account"
          searchTerms="subscription billing plan cancel upgrade premium share trial stripe revenuecat apple google"
          glassCard={glassCard}
          testId="settings-subscription-section"
          icon={<CreditCard className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
          title={t('settings.subscription', 'Subscription')}
          subtitle={tier === 'premium' || tier === 'paid' ? 'Premium' : tier === 'share' ? 'Share' : t('account.included', 'Free')}
        >
          {isPaid && (subscriptionInfo?.status !== 'none' || tier === 'paid' || tier === 'premium' || tier === 'share') ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-2xl" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }} data-testid="current-plan-info">
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{tier === 'premium' ? 'Premium' : tier === 'share' ? 'Share' : tier === 'paid' ? 'Premium' : 'Free'} {t('account.plan_label')}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                    {isShareMember ? `Member of ${shareOwnerName || "someone"}'s plan` : subscriptionInfo?.stripe_subscription_id || subscriptionInfo?.source ? (tier === 'premium' || tier === 'paid' ? '$9.99' : tier === 'share' ? '$19.99' : '$0') + t('account.per_month') : t('account.included')}
                  </p>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase" style={{
                  background: subscription?.is_trial ? 'rgba(59,130,246,0.15)' : subscriptionInfo?.cancel_at_period_end ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                  color: subscription?.is_trial ? '#3b82f6' : subscriptionInfo?.cancel_at_period_end ? '#ef4444' : '#22c55e',
                }} data-testid="subscription-status-badge">
                  {subscription?.is_trial ? t('account.trial', 'Trial') : subscriptionInfo?.cancel_at_period_end ? t('account.canceling') : t('account.active')}
                </span>
              </div>

              {subscription?.is_trial && subscription?.trial_days_remaining !== null && (
                <div className="p-4 rounded-2xl text-center space-y-2" style={{ background: subscription.trial_days_remaining <= 3 ? 'rgba(251,191,36,0.1)' : 'rgba(59,130,246,0.08)', border: `1px solid ${subscription.trial_days_remaining <= 3 ? 'rgba(251,191,36,0.3)' : 'rgba(59,130,246,0.2)'}` }} data-testid="trial-countdown-notice">
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="h-4 w-4" style={{ color: subscription.trial_days_remaining <= 3 ? '#fbbf24' : '#3b82f6' }} />
                    <p className="text-sm font-medium" style={{ color: subscription.trial_days_remaining <= 3 ? '#fbbf24' : '#3b82f6' }}>
                      {subscription.trial_days_remaining} {subscription.trial_days_remaining === 1 ? 'day' : 'days'} {t('generated.accounttab_left_on_your_501b')}
                    </p>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                    {subscription.trial_days_remaining <= 3 ? 'Upgrade now to keep your premium features!' : `Your ${subscription.trial_total_days || 7}-day free trial ends on ${subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : 'soon'}`}
                  </p>
                  <Button onClick={() => navigate('/plans')} className="mt-2 rounded-xl py-2 px-4 text-xs font-medium" style={{ background: 'var(--theme-accent)', color: '#fff' }} data-testid="trial-upgrade-btn">
                    <Sparkles className="h-3 w-3 mr-1.5" />{t('generated.accounttab_upgrade_now_a03e')}
                  </Button>
                </div>
              )}

              {subscriptionInfo?.current_period_end && !subscriptionInfo?.cancel_at_period_end && (
                <p className="text-xs text-center" style={{ color: 'var(--theme-textSecondary)' }} data-testid="period-end-info">{t('account.next_billing', { date: new Date(subscriptionInfo.current_period_end).toLocaleDateString() })}</p>
              )}

              {subscriptionInfo?.cancel_at_period_end && subscriptionInfo?.current_period_end && (
                <div className="p-4 rounded-2xl text-center space-y-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="cancellation-notice">
                  <p className="text-sm font-medium" style={{ color: '#ef4444' }}>{t('account.subscription_cancelled', 'Subscription Cancelled')}</p>
                  <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.revert_to_free', 'You will be reverted to the Free plan on {{date}}', { date: new Date(subscriptionInfo.current_period_end).toLocaleDateString() })}</p>
                  <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.keep_access_until', 'You\'ll keep full access until then.')}</p>
                </div>
              )}

              {/* Change Plan — visible for all subscription sources */}
              {hasActiveSub && !subscriptionInfo?.cancel_at_period_end && (
                <Button onClick={() => navigate('/plans')} className="w-full rounded-xl py-4 text-sm font-medium gap-2" style={{ background: 'var(--theme-accent)', color: '#fff' }} data-testid="change-plan-btn">
                  <RefreshCw className="h-4 w-4" />{t('account.change_plan', 'Change Plan')}
                </Button>
              )}

              {/* Cancel — Stripe subscriptions */}
              {isStripeSub && !subscriptionInfo?.cancel_at_period_end && (
                <Button variant="ghost" onClick={handleCancelSubscription} disabled={cancelLoading} className="w-full rounded-xl text-sm" style={{ color: 'var(--theme-textSecondary)' }} data-testid="cancel-subscription-btn">
                  {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{t('account.cancel_subscription')}
                </Button>
              )}

              {/* Manage — RevenueCat / App Store subscriptions */}
              {isRevenueCatSub && !isStripeSub && !subscriptionInfo?.cancel_at_period_end && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    const platform = subscriptionInfo?.platform;
                    if (platform === 'android') {
                      window.open('https://play.google.com/store/account/subscriptions', '_system');
                    } else {
                      // itms-apps:// opens the App Store subscription management natively
                      window.location.href = 'itms-apps://apps.apple.com/account/subscriptions';
                    }
                  }}
                  className="w-full rounded-xl text-sm"
                  style={{ color: 'var(--theme-textSecondary)' }}
                  data-testid="manage-store-subscription-btn"
                >
                  {subscriptionInfo?.platform === 'android'
                    ? t('account.manage_play_store', 'Manage in Google Play')
                    : t('account.manage_app_store', 'Manage Subscription')}
                </Button>
              )}

              {/* Resubscribe — any source after cancellation */}
              {(isStripeSub || isRevenueCatSub) && subscriptionInfo?.cancel_at_period_end && (
                <Button onClick={() => navigate('/plans')} className="w-full rounded-xl py-4 text-sm font-medium gap-2" style={{ background: 'var(--theme-accent)', color: '#fff' }} data-testid="resubscribe-btn">
                  <RefreshCw className="h-4 w-4" />{t('account.resubscribe', 'Resubscribe')}
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-2">
              <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>{t('account.free_plan_msg')}</p>
              <Button onClick={() => navigate('/plans')} className="w-full sm:w-auto gap-2 rounded-xl py-4 px-6 font-medium" style={{ background: 'var(--theme-accent)', color: '#fff' }} data-testid="upgrade-plan-btn">
                <Sparkles className="h-4 w-4" />{t('account.view_plans')}
              </Button>
            </div>
          )}

          {/* Restore Purchases — required by App Store guideline 3.1.1.
              Visible on iOS / Android native wrappers regardless of current
              tier so users who reinstalled or signed in on a new device can
              recover their entitlement with a single explicit tap. */}
          {isNativePlatform && (
            <div
              className="mt-5 pt-5 space-y-2 text-center"
              style={{ borderTop: '1px solid var(--glass-border)' }}
            >
              <Button
                variant="ghost"
                onClick={handleRestorePurchases}
                disabled={restoringPurchases}
                className="w-full rounded-xl py-3 text-sm font-medium flex items-center justify-center gap-2"
                style={{ color: 'var(--theme-text)' }}
                data-testid="settings-restore-purchases-btn"
              >
                {restoringPurchases ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {t('plans.restore_purchases', 'Restore Purchases')}
              </Button>
              <p className="text-[11px] leading-relaxed px-2" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('account.restore_help',
                  'If you previously subscribed and reinstalled the app or signed in on a new device, tap to restore your purchase.')}
              </p>
            </div>
          )}
        </CollapsibleSettingsCard>
      )}
      {/* Share Plan Management */}
      {isShareOwner && !user?.is_guest && (
        <div className="rounded-3xl p-6" style={glassCard} data-testid="settings-share-plan-section">
          <SharePlanManager />
        </div>
      )}
      {/* Device Management */}
      {isPaid && !user?.is_guest && (
        <CollapsibleSettingsCard
          storageKey="devices"
          tabId="account"
          searchTerms="devices my devices iphone ipad android tablet phone laptop sessions"
          glassCard={glassCard}
          testId="settings-devices-section"
          icon={<Smartphone className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
          title={t('settings.devices', 'My Devices')}
          rightAdornment={
            <span className="px-2.5 py-1 rounded-full text-xs font-medium mr-2" style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-text)' }} data-testid="device-count-badge">{devices.length} / {maxDevices}</span>
          }
        >
          <div className="flex gap-1.5 mb-5" data-testid="device-slots">
            {Array.from({ length: maxDevices }).map((_, i) => (
              <div key={`slot-${i}`} className="h-2 flex-1 rounded-full transition-all" style={{ background: i < devices.length ? 'var(--theme-accent)' : 'var(--glass-border)', opacity: i < devices.length ? 1 : 0.5 }} />
            ))}
          </div>
          {devicesLoading ? (
            <div className="text-center py-6" style={{ color: 'var(--theme-textSecondary)' }}>
              <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full mx-auto mb-2" />
              <p className="text-xs">{t('settings.loading_devices', 'Loading devices...')}</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-6" data-testid="no-devices-message">
              <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.no_devices', 'No devices registered yet.')}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-textSecondary)', opacity: 0.7 }}>{t('settings.devices_hint', 'Devices are registered when you sign in from a new device.')}</p>
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="device-list">
              {devices.map((device) => {
                const PlatformIcon = device.platform === 'ios' || device.platform === 'android' ? Smartphone : device.platform === 'tablet' ? Tablet : Monitor;
                const isRemoving = removingDevice === device.device_id;
                const isCurrentDevice = device.device_id === currentDeviceId;
                return (
                  <div key={device.device_id} className="flex items-center gap-3 p-3 rounded-xl transition-all" style={{ background: isCurrentDevice ? 'var(--glass-bg-active)' : 'var(--glass-bg)', border: isCurrentDevice ? '1.5px solid var(--theme-accent)' : '1px solid var(--glass-border)' }} data-testid={`device-item-${device.device_id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: isCurrentDevice ? 'var(--theme-accent)' : 'var(--glass-bg-active)' }}>
                      <PlatformIcon className="h-4 w-4" style={{ color: isCurrentDevice ? '#fff' : 'var(--theme-accent)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {editingDeviceId === device.device_id ? (
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <input
                              autoFocus
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditingDeviceId(null); }}
                              maxLength={40}
                              className="flex-1 min-w-0 px-2 py-1 rounded-lg text-sm font-medium outline-none"
                              style={{ background: 'var(--glass-bg)', border: '1.5px solid var(--theme-accent)', color: 'var(--theme-text)' }}
                              data-testid={`rename-device-input-${device.device_id}`}
                            />
                            <button onClick={saveRename} className="p-1 rounded-md hover:bg-green-500/10" data-testid={`rename-device-save-${device.device_id}`}>
                              <Check className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>{device.device_name || t('settings.unknown_device', 'Unknown Device')}</p>
                            <button onClick={() => startRename(device)} className="p-1 rounded-md hover:bg-[var(--glass-bg-active)] shrink-0" title={t('settings.rename_device', 'Rename')} data-testid={`rename-device-${device.device_id}`}>
                              <Pencil className="h-3 w-3" style={{ color: 'var(--theme-textSecondary)' }} />
                            </button>
                          </>
                        )}
                        {isCurrentDevice && editingDeviceId !== device.device_id && <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold tracking-wide uppercase shrink-0" style={{ background: 'var(--theme-accent)', color: '#fff' }} data-testid="current-device-badge">{t('settings.this_device', 'This device')}</span>}
                      </div>
                      <p className="text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>
                        {device.platform?.charAt(0).toUpperCase() + device.platform?.slice(1) || 'Web'}
                        {device.last_active && <> &middot; {t('settings.last_active', 'Last active')} {new Date(device.last_active).toLocaleDateString()}</>}
                      </p>
                    </div>
                    {!isCurrentDevice && (
                      <button onClick={() => removeDevice(device.device_id)} disabled={isRemoving} className="p-2 rounded-lg transition-all hover:bg-red-500/10 disabled:opacity-40" title={t('settings.remove_device', 'Remove device')} data-testid={`remove-device-${device.device_id}`}>
                        {isRemoving ? <div className="animate-spin h-4 w-4 border-2 border-red-400 border-t-transparent rounded-full" /> : <Trash2 className="h-4 w-4 text-red-400" />}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] mt-4 text-center" style={{ color: 'var(--theme-textSecondary)', opacity: 0.6 }}>{t(maxDevices === 1 ? 'settings.device_limit_info' : 'settings.device_limit_info_plural', { limit: maxDevices })}</p>
        </CollapsibleSettingsCard>
      )}
      {/* Legal & Licenses */}
      <CollapsibleSettingsCard
        storageKey="legal"
        tabId="account"
        searchTerms="legal privacy policy terms licenses bible translations copyright disclaimer"
        glassCard={glassCard}
        testId="settings-legal-section"
        defaultOpen={false}
        icon={<Scale className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
        title={t('settings.legal', 'Legal')}
        subtitle={t('settings.legal_subtitle', 'Privacy Policy, Terms, & Bible licenses')}
      >
        <p className="text-xs mb-4" style={{ color: 'var(--theme-textSecondary)', lineHeight: '1.5' }}>{t('settings.legal_disclaimer', 'Eili is your biblical study companion. Responses are generated for educational purposes and should be verified against original sources. This app does not replace professional theological counsel.')}</p>
        <button className="w-full flex items-center justify-between p-3 rounded-xl text-left transition-all" style={{ background: showLicenses ? 'var(--glass-bg-active)' : 'transparent', border: '1px solid var(--glass-border)' }} onClick={() => setShowLicenses(!showLicenses)} data-testid="settings-licenses-toggle">
          <span className="text-xs font-medium" style={{ color: 'var(--theme-text)' }}>{t('settings.bible_licenses', 'Bible Translation Licenses')}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${showLicenses ? 'rotate-180' : ''}`} style={{ color: 'var(--theme-textSecondary)' }} />
        </button>
        {showLicenses && (
          <div className="mt-3 space-y-3 max-h-64 overflow-y-auto pr-1" data-testid="settings-licenses-list">
            {mainTranslations.map((id) => {
              const credit = translationCredits[id];
              if (!credit) return null;
              const isPublicDomain = credit.notice === 'Public Domain';
              return (
                <div key={id} className="text-xs" style={{ color: 'var(--theme-textSecondary)', lineHeight: '1.5' }}>
                  <span className="font-semibold" style={{ color: 'var(--theme-text)' }}>{credit.name} ({id.toUpperCase()})</span>
                  {isPublicDomain ? <span className="ml-1.5 opacity-60">— {t('licenses.public_domain')}</span> : <p className="mt-0.5 opacity-75">{credit.notice}</p>}
                </div>
              );
            })}
          </div>
        )}
        <a href="/privacy" className="mt-3 w-full flex items-center justify-between p-3 rounded-xl text-left transition-all" style={{ border: '1px solid var(--glass-border)' }} data-testid="settings-privacy-link">
          <span className="text-xs font-medium" style={{ color: 'var(--theme-text)' }}>{t('settings.privacy_policy', 'Privacy Policy')}</span>
          <ChevronDown className="h-4 w-4 -rotate-90" style={{ color: 'var(--theme-textSecondary)' }} />
        </a>
      </CollapsibleSettingsCard>
      {/* App Tour */}
      <div className="rounded-3xl p-6" style={glassCard} data-testid="settings-app-tour-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--glass-bg-active)' }}>
              <Sparkles className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.app_tour', 'App Tour')}</h2>
              <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.app_tour_desc', 'Replay the guided walkthrough of key features')}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-xl text-xs shrink-0" style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-accent)' }}
            onClick={async () => {
              localStorage.removeItem('eili_tutorial_done');
              try { await axios.post(`${API}/tutorials/reset`, {}, { withCredentials: true }); } catch {}
              window.dispatchEvent(new CustomEvent('replay-tutorial'));
              if (onClose) onClose();
              toast.success(t('settings.tour_starting', 'Starting app tour...'));
            }}
            data-testid="settings-replay-tour-btn"
          >
            {t('settings.replay_tour', 'Replay Tour')}
          </Button>
        </div>
      </div>
      {/* Check for Updates — manual refresh for users stuck on a stale cache/SW */}
      <div className="rounded-3xl p-6" style={glassCard} data-testid="settings-check-updates-section">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--glass-bg-active)' }}>
              <RefreshCw className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.check_updates', 'Check for Updates')}</h2>
              <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.check_updates_desc', 'Force-refresh to pick up the latest version (clears local cache).')}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl text-xs shrink-0"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-accent)' }}
            onClick={async () => {
              toast.info(t('settings.checking_updates', 'Checking for updates…'));
              try {
                if (typeof window !== 'undefined' && typeof window.__eiliForceRefresh === 'function') {
                  await window.__eiliForceRefresh();
                  return;
                }
                // Fallback: manual cache + SW purge
                if ('serviceWorker' in navigator) {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
                }
                if ('caches' in window) {
                  const keys = await caches.keys();
                  await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
                }
              } finally {
                window.location.reload();
              }
            }}
            data-testid="settings-check-updates-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            {t('settings.refresh_now', 'Refresh Now')}
          </Button>
        </div>
      </div>
      {/* Support */}
      <CollapsibleSettingsCard
        storageKey="support"
        tabId="account"
        searchTerms="support feedback help report problem feature request bug email contact faq questions"
        glassCard={glassCard}
        testId="settings-support-section"
        defaultOpen={false}
        icon={<Bug className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
        title={t('settings.support', 'Support & Feedback')}
        subtitle={t('settings.support_subtitle', 'Report problems or request features')}
      >
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => navigate('/faq')}
            className="flex items-center gap-3 w-full rounded-xl px-4 py-3 text-sm font-medium transition-colors hover:bg-[var(--theme-hover)]"
            style={{ color: 'var(--theme-text)' }}
            data-testid="open-faq-btn"
          >
            <LifeBuoy className="h-4 w-4 shrink-0" style={{ color: 'var(--theme-accent)' }} />
            <div className="text-left">
              <span>{t('settings.help_faq', 'Help & FAQ')}</span>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('settings.help_faq_desc', 'Answers, walk-throughs & screenshots')}
              </p>
            </div>
          </button>
          <a href="mailto:report@askeili.com?subject=Problem Report" className="flex items-center gap-3 w-full rounded-xl px-4 py-3 text-sm font-medium transition-colors hover:bg-[var(--theme-hover)]" style={{ color: 'var(--theme-text)' }} data-testid="report-problem-btn">
            <Bug className="h-4 w-4 shrink-0" style={{ color: 'var(--theme-accent)' }} />
            <div className="text-left"><span>{t('settings.report_problem', 'Report a Problem')}</span><p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.report_problem_desc', 'Let us know about any issues')}</p></div>
          </a>
          <a href="mailto:requests@askeili.com?subject=Feature Request" className="flex items-center gap-3 w-full rounded-xl px-4 py-3 text-sm font-medium transition-colors hover:bg-[var(--theme-hover)]" style={{ color: 'var(--theme-text)' }} data-testid="request-feature-btn">
            <Lightbulb className="h-4 w-4 shrink-0" style={{ color: 'var(--theme-accent)' }} />
            <div className="text-left"><span>{t('settings.request_feature', 'Request a Feature')}</span><p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.request_feature_desc', 'Suggest new features or improvements')}</p></div>
          </a>
        </div>
      </CollapsibleSettingsCard>
      {/* Admin */}
      {hasAdminAccess && (
        <div className="rounded-3xl p-6" style={glassCard} data-testid="admin-console-access-section">
          <SectionHeader icon={ShieldAlert} title={t('generated.accounttab_administration_7b30')} />
          <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.accounttab_you_have_admin_7789')}</p>
          <Button onClick={() => window.open('/admin', '_blank')} className="w-full sm:w-auto rounded-xl gap-2 bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="open-admin-console-btn">
            <ShieldAlert className="h-4 w-4" />{t('generated.accounttab_open_admin_console_a370')}
          </Button>
        </div>
      )}
      {/* Logout */}
      <div className="rounded-3xl p-6" style={glassCard} data-testid="settings-account-section">
        <SectionHeader icon={LogOut} title={t('settings.account')} />
        <Button variant="destructive" onClick={async () => { try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); navigate('/login'); } catch { navigate('/login'); } }} className="w-full sm:w-auto rounded-xl" data-testid="settings-logout-button">{t('settings.logout')}</Button>
      </div>
      {/* Delete Account */}
      {!user?.is_guest && (
        <div className="rounded-3xl p-6" style={{ ...glassCard, borderColor: 'rgba(239,68,68,0.15)' }} data-testid="settings-delete-account-section">
          <SectionHeader icon={Trash2} title={t('settings.delete_account', 'Delete Account')} />
          <p className="text-xs mb-3" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.delete_account_desc', 'Permanently delete your account and all associated data. You can reactivate within 30 days.')}</p>
          <Button variant="outline" onClick={() => setShowDeleteModal(true)} className="w-full sm:w-auto rounded-xl border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="settings-delete-account-btn">
            <Trash2 className="h-4 w-4 mr-2" />{t('settings.delete_my_account', 'Delete My Account')}
          </Button>
        </div>
      )}
    </>
  );
}
